import React, { type ReactNode } from 'react';

import {
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import { type SubagentDraftProgress } from '@apps/web/components/phoebe_chat/streaming_types';
import { parseStructuredReportFromOutput } from '@apps/web/components/phoebe_chat_view/artifacts/structured_report_artifacts';
import {
    CaregiverMemoryLookupCards,
    CaregiverMemorySearchCards,
} from '@apps/web/components/phoebe_chat_view/tool_calls/caregiver_memory_tool_ui';
import {
    CONVERSATION_CONTROL_TOOL_OUTPUTS,
    ConversationControlCards,
    MonitoringActionCard,
    NotificationActionCard,
    OutreachReviewActionCard,
    ResponseOverrideActionCard,
    StagedMessageActionCard,
    SubscriberActionCard,
    SuggestionFeedbackActionCard,
} from '@apps/web/components/phoebe_chat_view/tool_calls/conversation_control_tool_ui';
import {
    CaregiverDetailsCard,
    ClientDetailsCard,
    OutreachDetailsCard,
    ShiftDetailsCard,
} from '@apps/web/components/phoebe_chat_view/tool_calls/detail_tool_ui';
import {
    CurrentDatetimeCard,
    ExecutionPlanUpdateCard,
    OrganizationContextCard,
    StructuredReportSummaryCard,
} from '@apps/web/components/phoebe_chat_view/tool_calls/infrastructure_tool_ui';
import {
    AssignShiftsActionCard,
    COMPACT_OUTREACH_SUGGESTION_WRITE_TOOLS,
    ContactAttemptsActionCard,
    DraftOutreachActionCard,
    FillCalloutShiftsActionCard,
    OutreachFilterRuleActionCard,
    OutreachStatusActionCard,
    SuggestionMutationActionCard,
} from '@apps/web/components/phoebe_chat_view/tool_calls/outreach_action_tool_ui';
import {
    AutoDraftGroupingCards,
    RecommendationDetailCards,
    RecommendationFitCards,
    SubagentDraftRows,
    SuggestionDraftCards,
} from '@apps/web/components/phoebe_chat_view/tool_calls/recommendation_tool_ui';
import {
    AVAILABILITY_ACTION_TOOL_NAMES,
    AvailabilityActionCard,
    getActionError,
    MEMORY_ACTION_TOOL_NAMES,
    MemoryActionCard,
    RECORD_ACTION_TOOL_NAMES,
    RecordUpdateActionCard,
    SaveNoteActionCard,
} from '@apps/web/components/phoebe_chat_view/tool_calls/record_settings_tool_ui';
import {
    CaregiverAttributeSummaryCards,
    CaregiverSearchCards,
    ClientSearchCards,
    ConversationHistorySearchCards,
    OutreachSearchCards,
    ShiftSearchCards,
    VoiceTranscriptSearchCards,
} from '@apps/web/components/phoebe_chat_view/tool_calls/search_tool_ui';
import { SkillContextToolCard } from '@apps/web/components/phoebe_chat_view/tool_calls/skill_context_tool_ui';
import { ToolIssueCard } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { EXECUTION_PLAN_TOOL_NAME } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_name_constants';

export function renderToolLabel(
    baseLabel: string,
    args: Record<string, unknown> | Array<unknown> | null | undefined,
    formatName: (name: string | null | undefined) => string,
    onCaregiverClick?: (caregiverId: string) => void,
    output?: unknown,
): ReactNode {
    if (!args || Array.isArray(args)) {
        return baseLabel;
    }

    const caregiversArg = Array.isArray(args['caregivers'])
        ? args['caregivers']
        : [];
    if (caregiversArg.length === 1 && isRecord(caregiversArg[0])) {
        const caregiverName = getStringValue(
            caregiversArg[0],
            'caregiver_name',
        );
        if (caregiverName) {
            return `${baseLabel}: ${caregiverName}`;
        }
    }

    if (
        baseLabel === 'Searched caregiver memories' &&
        typeof args['query'] === 'string' &&
        args['query'].trim().length > 0
    ) {
        return `${baseLabel}: ${args['query'].trim()}`;
    }

    // Show client name from tool output for client details
    if (output && typeof output === 'object' && !Array.isArray(output)) {
        const data = output as Record<string, unknown>;
        const firstName =
            typeof data['first_name'] === 'string' ? data['first_name'] : null;
        const lastName =
            typeof data['last_name'] === 'string' ? data['last_name'] : null;
        if (firstName && lastName) {
            return `${baseLabel}: ${formatName(`${firstName} ${lastName}`)}`;
        }
    }

    const caregiverName =
        typeof args['caregiver_name'] === 'string'
            ? args['caregiver_name']
            : null;
    if (!caregiverName) {
        return baseLabel;
    }
    const caregiverId =
        typeof args['caregiver_id'] === 'string' ? args['caregiver_id'] : null;
    if (caregiverId && onCaregiverClick) {
        return (
            <React.Fragment>
                {baseLabel}:{' '}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCaregiverClick(caregiverId);
                    }}
                    className="text-primary decoration-primary/40 hover:decoration-primary cursor-pointer underline underline-offset-2"
                >
                    {caregiverName}
                </button>
            </React.Fragment>
        );
    }
    return `${baseLabel}: ${caregiverName}`;
}

export function renderMonitoringToolLabel({
    isComplete,
}: {
    readonly isComplete: boolean;
}): string {
    if (!isComplete) {
        return 'Scheduling outreach monitoring';
    }

    return 'Scheduled outreach monitoring';
}

export type ToolSummaryItem = {
    readonly label: string;
    readonly value: string | ReactNode;
};

export type ToolSummary = {
    readonly count?: number;
    readonly items: Array<ToolSummaryItem>;
};

// A verbose recommendations call under the subagents flag returns a fan-out
// receipt whose decisions stream in live. Those cards are the payload the
// user is waiting on, so the tool row auto-expands instead of hiding them
// behind a collapsed chevron.
export function isRecommendationFanoutReceipt(
    toolName: string,
    output: unknown,
): boolean {
    return (
        (toolName === 'get_shift_recommendations' ||
            toolName === 'get_recommendations' ||
            toolName === 'get_group_recommendations') &&
        isRecord(output) &&
        getStringValue(output, 'status') ===
            'recommendation_candidate_fanout_started'
    );
}

export function formatToolOutput(
    toolName: string,
    output: unknown,
    onCaregiverClick?: (caregiverId: string) => void,
    onClientClick?: (clientId: string) => void,
    args?: Record<string, unknown> | Array<unknown> | null,
): ToolSummary | null {
    if (toolName === 'view_skills' || toolName === 'load_skill') {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <SkillContextToolCard
                            toolName={toolName}
                            output={output}
                            args={args}
                        />
                    ),
                },
            ],
        };
    }

    if (!output || typeof output !== 'object') {
        return null;
    }

    const data = output as Record<string, unknown>;

    // Structured-report tools also render as full table artifacts after
    // the assistant text. The tool card stays compact and points to that
    // artifact instead of dumping columns/rows inline.
    const structuredReport = parseStructuredReportFromOutput(output);
    if (structuredReport) {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <StructuredReportSummaryCard
                            toolName={toolName}
                            report={structuredReport}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'caregivers_details') {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <CaregiverDetailsCard
                            data={data}
                            onCaregiverClick={onCaregiverClick}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'caregivers_attribute_summary') {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: (
                        <CaregiverAttributeSummaryCards data={dataWithArgs} />
                    ),
                },
            ],
        };
    }

    if (toolName === 'client_details' || toolName === 'get_client_details') {
        return {
            items: [{ label: '', value: <ClientDetailsCard data={data} /> }],
        };
    }

    if (toolName === 'shifts_details') {
        return {
            items: [{ label: '', value: <ShiftDetailsCard data={data} /> }],
        };
    }

    if (
        toolName === 'callouts_details' ||
        toolName === 'get_outreach_details'
    ) {
        return {
            items: [{ label: '', value: <OutreachDetailsCard data={data} /> }],
        };
    }

    if (toolName === 'organization_scheduling_context') {
        return {
            items: [
                {
                    label: '',
                    value: <OrganizationContextCard data={data} />,
                },
            ],
        };
    }

    if (toolName === 'date_and_time' || toolName === 'get_current_datetime') {
        return {
            items: [{ label: '', value: <CurrentDatetimeCard data={data} /> }],
        };
    }

    if (toolName === EXECUTION_PLAN_TOOL_NAME) {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <ExecutionPlanUpdateCard
                            data={data}
                            args={isRecord(args) ? args : null}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'save_note') {
        return {
            items: [
                {
                    label: '',
                    value: <SaveNoteActionCard data={data} />,
                },
            ],
        };
    }

    if (toolName === 'draft_agent_outreach') {
        if (getActionError(data)) {
            return null;
        }
        return {
            items: [
                { label: '', value: <DraftOutreachActionCard data={data} /> },
            ],
        };
    }

    if (
        toolName === 'manage_outreach_add_suggestions' ||
        toolName === 'manage_outreach_remove_suggestions'
    ) {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: (
                        <SuggestionMutationActionCard
                            data={dataWithArgs}
                            args={isRecord(args) ? args : null}
                            action={
                                toolName === 'manage_outreach_add_suggestions'
                                    ? 'add'
                                    : 'remove'
                            }
                        />
                    ),
                },
            ],
        };
    }

    if (
        toolName === 'manage_outreach_set_status' ||
        toolName === 'manage_outreach_pause' ||
        toolName === 'manage_outreach_resume' ||
        toolName === 'manage_outreach_cancel'
    ) {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <OutreachStatusActionCard
                            data={data}
                            args={isRecord(args) ? args : null}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'update_outreach_filter_rule') {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <OutreachFilterRuleActionCard
                            data={data}
                            args={isRecord(args) ? args : null}
                        />
                    ),
                },
            ],
        };
    }

    if (
        toolName === 'manage_outreach_start_with_contacts' ||
        toolName === 'manage_outreach_add_contact_attempts'
    ) {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <ContactAttemptsActionCard
                            data={data}
                            started={
                                toolName ===
                                'manage_outreach_start_with_contacts'
                            }
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'fill_callout_shifts') {
        return {
            items: [
                {
                    label: '',
                    value: <FillCalloutShiftsActionCard data={data} />,
                },
            ],
        };
    }

    if (toolName === 'assign_shifts_to_caregiver') {
        return {
            items: [
                {
                    label: '',
                    value: <AssignShiftsActionCard data={data} />,
                },
            ],
        };
    }

    if (RECORD_ACTION_TOOL_NAMES.has(toolName)) {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <RecordUpdateActionCard
                            toolName={toolName}
                            data={data}
                            args={isRecord(args) ? args : null}
                        />
                    ),
                },
            ],
        };
    }

    if (AVAILABILITY_ACTION_TOOL_NAMES.has(toolName)) {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <AvailabilityActionCard
                            toolName={toolName}
                            data={data}
                            args={isRecord(args) ? args : null}
                        />
                    ),
                },
            ],
        };
    }

    if (MEMORY_ACTION_TOOL_NAMES.has(toolName)) {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <MemoryActionCard
                            toolName={toolName}
                            data={data}
                            args={isRecord(args) ? args : null}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'monitor_outreach') {
        return {
            items: [{ label: '', value: <MonitoringActionCard data={data} /> }],
        };
    }

    if (toolName === 'review_outreach') {
        return {
            items: [
                { label: '', value: <OutreachReviewActionCard data={data} /> },
            ],
        };
    }

    if (toolName === 'notify') {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <NotificationActionCard
                            data={data}
                            args={isRecord(args) ? args : null}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'override_contact_attempt_response') {
        return {
            items: [
                {
                    label: '',
                    value: <ResponseOverrideActionCard data={data} />,
                },
            ],
        };
    }

    if (toolName === 'submit_outreach_suggestion_feedback') {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <SuggestionFeedbackActionCard
                            data={data}
                            args={isRecord(args) ? args : null}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'add_outreach_subscriber') {
        return {
            items: [{ label: '', value: <SubscriberActionCard data={data} /> }],
        };
    }

    if (
        toolName === 'approve_staged_message' ||
        toolName === 'reject_staged_message' ||
        toolName === 'edit_staged_message_content' ||
        toolName === 'send_one_off_sms_in_conversation'
    ) {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <StagedMessageActionCard
                            toolName={toolName}
                            data={data}
                            args={isRecord(args) ? args : null}
                        />
                    ),
                },
            ],
        };
    }

    if (COMPACT_OUTREACH_SUGGESTION_WRITE_TOOLS.has(toolName)) {
        const status =
            typeof data['status'] === 'string' ? data['status'].trim() : null;
        const error =
            typeof data['error'] === 'string' ? data['error'].trim() : null;
        if (error) {
            return {
                items: [
                    {
                        label: '',
                        value: (
                            <ToolIssueCard
                                title="Suggestion update needs review"
                                error={error}
                            />
                        ),
                    },
                ],
            };
        }
        const value = status || error;
        return value ? { items: [{ label: '', value }] } : null;
    }

    if (toolName === 'get_caregiver_memory') {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: (
                        <CaregiverMemoryLookupCards
                            data={dataWithArgs}
                            onCaregiverClick={onCaregiverClick}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'search_caregiver_memories') {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: (
                        <CaregiverMemorySearchCards
                            data={dataWithArgs}
                            onCaregiverClick={onCaregiverClick}
                        />
                    ),
                },
            ],
        };
    }

    if (
        toolName === 'get_shift_recommendations' ||
        toolName === 'get_recommendations' ||
        toolName === 'get_group_recommendations'
    ) {
        // A verbose call under the recommendation-subagents flag returns a
        // fan-out receipt instead of caregiver rows. Its decision carousel
        // renders as a first-class timeline element (see the tool node and
        // execution card renderers), not inside this expandable summary.
        if (isRecommendationFanoutReceipt(toolName, output)) {
            return null;
        }
        return {
            items: [
                {
                    label: '',
                    value: (
                        <RecommendationFitCards
                            data={data}
                            onCaregiverClick={onCaregiverClick}
                        />
                    ),
                },
            ],
        };
    }

    if (
        toolName === 'get_caregiver_recommendation_details' ||
        toolName === 'get_caregiver_group_recommendation_details'
    ) {
        return {
            items: [
                {
                    label: '',
                    value: (
                        <RecommendationDetailCards
                            data={data}
                            onCaregiverClick={onCaregiverClick}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'group_shifts_for_auto_draft') {
        return {
            items: [
                {
                    label: '',
                    value: <AutoDraftGroupingCards data={data} />,
                },
            ],
        };
    }

    if (toolName === 'caregivers_search') {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: (
                        <CaregiverSearchCards
                            data={dataWithArgs}
                            onCaregiverClick={onCaregiverClick}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'clients_search') {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: (
                        <ClientSearchCards
                            data={dataWithArgs}
                            onClientClick={onClientClick}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'shifts_search') {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: (
                        <ShiftSearchCards
                            data={dataWithArgs}
                            onCaregiverClick={onCaregiverClick}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'outreach_search' || toolName === 'callouts_search') {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: (
                        <OutreachSearchCards
                            data={dataWithArgs}
                            resultKind={
                                toolName === 'callouts_search'
                                    ? 'open-shift'
                                    : 'outreach'
                            }
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'search_conversation_history') {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: (
                        <ConversationHistorySearchCards data={dataWithArgs} />
                    ),
                },
            ],
        };
    }

    if (toolName === 'search_voice_call_transcripts') {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: <VoiceTranscriptSearchCards data={dataWithArgs} />,
                },
            ],
        };
    }

    if (CONVERSATION_CONTROL_TOOL_OUTPUTS.has(toolName)) {
        const dataWithArgs = {
            ...data,
            __tool_args: isRecord(args) ? args : null,
        };
        return {
            items: [
                {
                    label: '',
                    value: (
                        <ConversationControlCards
                            data={dataWithArgs}
                            toolName={toolName}
                        />
                    ),
                },
            ],
        };
    }

    if (toolName === 'get_outreach_suggestion_drafts') {
        const unitDrafts = Array.isArray(data['unit_drafts'])
            ? (data['unit_drafts'] as Array<unknown>)
            : Array.isArray(data['units'])
              ? (data['units'] as Array<unknown>)
              : [];
        if (unitDrafts.length === 0) {
            return null;
        }

        return {
            items: [
                {
                    label: '',
                    value: (
                        <SuggestionDraftCards
                            data={data}
                            unitDrafts={unitDrafts}
                        />
                    ),
                },
            ],
        };
    }

    return null;
}

export function buildStreamingSubagentDraftSummary(
    progress: SubagentDraftProgress,
): ToolSummary | null {
    if (progress.units.length === 0) {
        return null;
    }
    const unitDrafts = progress.units.map((unit) => {
        const unitId =
            typeof unit['unit_id'] === 'string' ? unit['unit_id'] : null;
        const completed = unitId
            ? progress.completedByUnitId.get(unitId)
            : undefined;
        if (completed) {
            return completed;
        }
        return { ...unit, __pending: true };
    });
    return {
        items: [
            {
                label: '',
                value: (
                    <SubagentDraftRows
                        unitDrafts={unitDrafts}
                        tracesByUnitId={progress.tracesByUnitId}
                    />
                ),
            },
        ],
    };
}
